import React, { useEffect, useState } from 'react';
import { Box, MenuItem, styled } from '@mui/material';

import CustomModal from 'src/components/modal/customModal';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFMultiSelect, RHFRadioGroup, RHFSelect, RHFTextField } from 'src/components/hook-form';
import { Stack } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import { Typography } from '@mui/material';
import { Button } from '@mui/material';
import { IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { PRODUCT_LIST } from 'src/config/commonConfig';
import Logger from 'src/lib/Logger';
import { axiosGetCall } from 'src/services/apisCall';
const logger = new Logger("create plan model");

const InputContainer = styled('div')(() => ({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: '20px',
    flexDirection: 'row',
}));

const InputInnerContainer = styled('div')(() => ({
    width: 'calc(100% / 2)',
}));

const PlanForm = ({ open, onClose, plan, edit, handlePlanUpdate, handleAddPlan }) => {
    const [loadingButton, setLoadingButton] = useState(false);
    const planSchema = Yup.object().shape({
        product_id: Yup.string()
            .transform((value) => (value ? value.trim() : ''))
            .required('Product Name is required'),
        plan_name: Yup.string()
            .transform((value) => (value ? value.trim() : ''))
            .required('Plan Name is required'),
        price: Yup.number()
        .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
        )
        .when('plan_type', {
            is: (val) => val !== 'bundle',
            then: (schema) =>
            schema.typeError('Price is invalid').required('Price is required'),
            otherwise: (schema) => schema.nullable().notRequired(),
        }),
        // min_price: Yup.number().typeError('Min Price is invalid'),
        // max_price: Yup.number().typeError('Max Price is invalid'),
        storage: Yup.number().when('plan_type', {
            is: (val) => val !== 'bundle',
            then: () => Yup.number().typeError('Storage is invalid').required('Storage is required'),
            otherwise: () => Yup.number().nullable().notRequired()
        }),
        api_calls: Yup.number().typeError('Api Calls is invalid').required('Api Calls is required'),
        team_size: Yup.string().transform((value) => (value ? value.trim() : '')).required('Team Size is required'),
        contacts: Yup.string().typeError('Contacts is invalid').required('Contacts is required'),
        attachments_limit: Yup.number()
        .typeError('Attachment Limit is invalid')
        .required('Attachment Limit is required'),
        attachments_limit_type: Yup.string().oneOf(['MB', 'GB']).required('Attachment Limit Type is required'),
        archive_storage: Yup.number().typeError('Archive storage is invalid').required('Archive storage is required'),
        archive_storage_type: Yup.string().oneOf(['MB', 'GB']).required('Archive storage Type is required'),
        min_user: Yup.number().typeError('Min User is invalid').required('Min User is required'),
        max_user: Yup.number().typeError('Max User is invalid').required('Max User is required'),
        currency: Yup.string()
            .transform((value) => (value ? value.trim() : ''))
            .required('Currency is required'),
        description: Yup.string()
            .transform((value) => (value ? value.trim() : ''))
            .required('Description is required'),
        order: Yup.number().typeError('Order is invalid').required('Order is required'),
        duration_months: Yup.number()
            .integer()
            .min(0, "Duration Months must be at least 0")
            .typeError('Duration Months is invalid')
            .required('Duration Months is required'),
        cgst: Yup.number().min(0, "CGST must be at least 0").integer().typeError('CGST is invalid').required('CGST is required'),
        sgst: Yup.number().min(0, "SGST must be at least 0").integer().typeError('SGST is invalid').required('SGST is required'),
        yearly_discount: Yup.number().when('plan_type', {
            is: (val) => val !== 'bundle',
            then: () => Yup.number().min(0).integer().typeError('Yearly Discount is invalid').required('Yearly Discount is required'),
            otherwise: () => Yup.number().nullable().notRequired()
        }),
        monthly_discount: Yup.number().when('plan_type', {
            is: (val) => val !== 'bundle',
            then: () => Yup.number().min(0).integer().typeError('Monthly Discount is invalid').required('Monthly Discount is required'),
            otherwise: () => Yup.number().nullable().notRequired()
        }),
        // features: Yup.array()
        //     .of(
        //         Yup.object().shape({
        //             feature_name: Yup.string()
        //                 .transform((value) => (value ? value.trim() : ''))
        //                 .required('Feature Name is required'),
        //             feature_description: Yup.string()
        //                 .transform((value) => (value ? value.trim() : ''))
        //                 .required('Feature Description is required'),
        //         })
        //     )
        //     .optional(),
        variations: Yup.array().when('plan_type', {
            is: 'bundle',
            then: () => Yup.array().of(
                Yup.object().shape({
                    storage: Yup.number().typeError('Storage is invalid').required('Storage is required'),
                    type: Yup.string().oneOf(['MB', 'GB']).required('Storage Type is required'),
                    price: Yup.number().typeError('Price is invalid').required('Price is required'),
                    monthly_discount: Yup.number().typeError('Monthly Discount is invalid').required('Monthly Discount is required'),
                    yearly_discount: Yup.number().typeError('Yearly Discount is invalid').required('Yearly Discount is required'),
                })
            ).min(1, 'At least one variation is required'),
            otherwise: () => Yup.array().nullable()
        }),
        slabs: Yup.array().when('product_id', {
            is: 'Broadcast',
            then: () => {
                const voiceSchema = Yup.array()
                    .of(
                        Yup.object().shape({
                            min_calls: Yup.number().required('min_calls is required'),
                            max_calls: Yup.number()
                                .required('max_calls is required')
                                .moreThan(Yup.ref('min_calls'), 'max_calls must be greater than min_calls'),
                        })
                    )
                    .required('Slabs are required for Voice products')
                    .min(1, "At least one call range is required")
                    .test(
                        'min_calls-greater-than-previous-max_calls',
                        'min_calls must be greater than the previous max_calls',
                        function (array) {
                            if (!array || array.length < 2) {
                                return true;
                            }
                            for (let i = 1; i < array.length; i++) {
                                if (array[i].min_calls <= array[i - 1].max_calls) {
                                    return this.createError({
                                        path: `${this.path}[${i}].min_calls`,
                                        message: `Min Calls (${array[i].min_calls}) must be greater than the previous Max Calls (${array[i - 1].max_calls})`,
                                    });
                                }
                            }
                            return true;
                        }
                    );
                console.log('Voice schema:', voiceSchema.describe()); // Log the schema for debugging
                return voiceSchema;
            },
            otherwise: () => {
                const nonVoiceSchema = Yup.array().nullable();
                console.log('Non-Voice schema:', nonVoiceSchema.describe()); // Log the schema for debugging
                return nonVoiceSchema;
            },
        }),
        combo_pack: Yup.array().when('plan_type', {
            is: 'bundle',
            then: () => {
                const comboSchema = Yup.array().min(2, 'Combo pack must contain at least 2 items for bundle plans').
                required();
                return comboSchema;
            },
            otherwise: () => {
                const nonVoiceSchema = Yup.array().nullable();
                console.log('Non-Voice schema:', nonVoiceSchema.describe()); // Log the schema for debugging
                return nonVoiceSchema;
            },
        })
    });

    const methods = useForm({
        resolver: yupResolver(planSchema),
        defaultValues: {
            slabs: [{ min_calls: '', max_calls: '', price: 0 }],
            combo_pack: [],
            plan_type: "individual",
            product_id: "Console",
            attachments_limit_type: 'GB',
            archive_storage_type: "GB",
            variations: [{ storage: '', type: 'GB', price: '', monthly_discount: '', yearly_discount: '' }]
        }
        // defaultValues: plan,
    });

    const { control } = methods;

    const { fields: slabField, append: slabAppend, remove: slabRemove } = useFieldArray({
        control,
        name: "slabs",
    })

    const { fields: variationsField, append: variationsAppend, remove: variationsRemove } = useFieldArray({
        control,
        name: "variations",
    })

    const { setValue, handleSubmit, watch, reset } = methods;

    const values = watch();
    // const onSubmit = async (data) => {
    //     edit ? await handlePlanUpdate(data, setLoadingButton) : await handleAddPlan(data, setLoadingButton);
    // };
    const onSubmit = async (data) => {
    const payload = {
        ...data,
        attachments_limit: {
        size: Number(data.attachments_limit),
        type: data.attachments_limit_type.toLowerCase(), // mb | gb
        },
        archive_storage: {
        size: Number(data.archive_storage),
        type: data.archive_storage_type.toLowerCase(), // mb | gb
        },
    };

    delete payload.attachments_limit_type;
    delete payload.archive_storage_type;

    edit
        ? await handlePlanUpdate(payload, setLoadingButton)
        : await handleAddPlan(payload, setLoadingButton);
    };

    const getPlanSlabs = async () => {
        try {
            const { slabs } = await axiosGetCall("slabs", { planId: plan?.plan_id });
            setValue('slabs', slabs);
        } catch (error) {
            logger.error(error);
        }
    }
    useEffect(() => {
        if (plan) {
            setValue('product_id', plan.product_id);
            setValue('combo_pack', (plan?.combo_pack?.included_products || []).map(({ product_id }) => product_id))
            setValue('plan_id', plan.plan_id);
            setValue('plan_name', plan.plan_name);
            setValue('price', plan.price);
            // setValue('min_price', plan.min_price);
            // setValue('max_price', plan.max_price);

            const archiveStore = plan.archive_storage || plan.storage;
            if (archiveStore && typeof archiveStore === 'object') {
                setValue('archive_storage', archiveStore.size);
                setValue('archive_storage_type', (archiveStore.type || 'GB').toUpperCase());
                setValue('storage', archiveStore.size); // Mapping to storage for schema compliance if needed
            } else {
                setValue('archive_storage', archiveStore);
                setValue('archive_storage_type', 'GB');
                setValue('storage', archiveStore);
            }

            setValue('api_calls', plan.api_calls);
            setValue('team_size', plan.team_size);
            setValue('contacts', plan.contacts);

            const attachLimit = plan.attachments_limit || plan.attachment_limit;
            if (attachLimit && typeof attachLimit === 'object') {
                setValue('attachments_limit', attachLimit.size);
                setValue('attachments_limit_type', (attachLimit.type || 'GB').toUpperCase());
            } else {
                setValue('attachments_limit', attachLimit);
                setValue('attachments_limit_type', 'GB');
            }

            setValue('min_user', plan.min_user);
            setValue('max_user', plan.max_user);
            setValue('currency', plan.currency);
            setValue('description', plan.description);
            setValue('order', plan.order);
            setValue('duration_months', plan.duration_months);
            setValue('cgst', plan.cgst);
            setValue('sgst', plan.sgst);
            setValue('yearly_discount', plan.yearly_discount);
            setValue('monthly_discount', plan.monthly_discount);
            // setValue('features', plan.features);
            setValue('plan_type', plan.plan_type)
            if (plan.variations) {
                const formattedVariations = plan.variations.map(v => {
                    let s = v.storage;
                    let t = 'GB';
                    if (s && typeof s === 'object') {
                        t = (s.type || 'GB').toUpperCase();
                        s = s.size;
                    }
                    return { ...v, storage: s, type: t };
                });
                setValue('variations', formattedVariations);
            }
        }
        if (plan?.product_id === "Broadcast") {
            getPlanSlabs();
        }
    }, [plan, reset]);


    const description = (
        <Box>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Stack width={'100%'} direction={'column'} gap={2} sx={{ p: '25px', overflowY: 'auto', height: '700px' }}>
                    <InputContainer>
                        <RHFRadioGroup label={"Plan Type"} row={true} name="plan_type" options={[{ label: "Individual", value: "individual" }, { label: "Combo Pack", value: "bundle" }]} />
                    </InputContainer>
                    <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="plan_name" label="Plan Name" placeholder="Enter Plan Name." />
                        </InputInnerContainer>

                        <InputInnerContainer>
                            {values?.plan_type === "bundle" ? <RHFMultiSelect
                                name="combo_pack"
                                label="Select Bundle Products"
                                control={control}
                                chip
                                checkbox
                                fullWidth
                                options={PRODUCT_LIST.map((ele) => ({ label: ele, value: ele }))}
                            /> : <RHFSelect
                                name="product_id" // The name of the field in the form
                                label="Select Product" // Label for the select dropdown
                                // helperText="Choose a product from the list"
                                control={control} // Pass the control object from useForm
                            >{
                                    PRODUCT_LIST.map((ele) => (
                                        <MenuItem value={ele}>{ele}</MenuItem>
                                    ))
                                }
                            </RHFSelect>}
                        </InputInnerContainer>
                    </InputContainer>

                    <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="price" label="Price" placeholder="Enter Price." type={'number'} />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField name="currency" label="Currency" placeholder="Enter Currency." />
                        </InputInnerContainer>
                    </InputContainer>

                    {/* <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="min_price" label="Min Price" placeholder="Enter Min Price." type={'number'} />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField name="max_price" label="Max Price" placeholder="Enter Max Price." type={'number'} />
                        </InputInnerContainer>
                    </InputContainer> */}

                    <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="min_user" label="Min User" placeholder="Enter Min User." type={'number'} />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField name="max_user" label="Max User" placeholder="Enter Max User." type={'number'} />
                        </InputInnerContainer>
                    </InputContainer>

                    <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="sgst" label="SGST" placeholder="Enter SGST." type={'number'} />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField name="cgst" label="CGST" placeholder="Enter CGST." type={'number'} />
                        </InputInnerContainer>
                    </InputContainer>

                    {values?.plan_type !== 'bundle' && <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField
                                name="monthly_discount"
                                label="Monthly Discount"
                                placeholder="Enter Monthly Discount."
                                type={'number'}
                            />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField
                                name="yearly_discount"
                                label="Yearly Discount"
                                placeholder="Enter Yearly Discount."
                                type={'number'}
                            />
                        </InputInnerContainer>
                    </InputContainer>}

                     <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="api_calls" label="Api Calls" placeholder="Enter Api Calls." type={'number'}/>
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField
                                name="duration_months"
                                label="Days in a Month"
                                placeholder="Enter Days in a Month."
                                type={'number'}
                            />
                        </InputInnerContainer>
                    </InputContainer>

                    <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="team_size" label="Team Size" placeholder="Enter Team Size." />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField name="contacts" label="Contacts" placeholder="Enter Contacts" type={'text'}/>
                        </InputInnerContainer>
                    </InputContainer>

                    <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField
                                name="description"
                                label="Description"
                                placeholder="Enter Description."
                                type={'text'}
                                multiline
                                rows={4}
                            />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFTextField
                                name="order"
                                label="Order"
                                placeholder="Enter Order."
                                type={'number'}
                                multiline
                                rows={4}
                            />
                        </InputInnerContainer>
                    </InputContainer>
                        
                        <InputContainer>
                        <InputInnerContainer>
                            <RHFTextField name="attachments_limit" label="Attachment Limit" placeholder="Enter Attachment Limit" type="number" />
                        </InputInnerContainer>

                        <InputInnerContainer>
                            <RHFSelect
                            name="attachments_limit_type"
                            label="Type"
                            control={control}
                            >
                            <MenuItem value="MB">MB</MenuItem>
                            <MenuItem value="GB">GB</MenuItem>
                            </RHFSelect>
                        </InputInnerContainer>
                        </InputContainer>
                    
                    <InputContainer>
                        <InputInnerContainer>
                                <RHFTextField name="archive_storage" label="Archive Storage" placeholder="Enter Archive Storage" type="number" />
                        </InputInnerContainer>
                        <InputInnerContainer>
                            <RHFSelect name="archive_storage_type" label="Type" control={control} >
                            <MenuItem value="MB">MB</MenuItem>
                            <MenuItem value="GB">GB</MenuItem>
                            </RHFSelect>
                        </InputInnerContainer>
                    </InputContainer>

                    {values?.plan_type === "bundle" && <React.Fragment> 
                    <InputContainer>
                        <Typography variant="h6">Storage Variations</Typography>
                        <Button onClick={() => variationsAppend({ storage: '', type: 'GB', price: '', monthly_discount: '', yearly_discount: '' })}>Add Variation</Button>
                    </InputContainer>
                    {variationsField.map((item, index) => (
                        <Box key={item.id} display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                            <InputInnerContainer>
                                <Controller
                                    name={`variations[${index}].storage`}
                                    control={control}
                                    defaultValue={item.storage || ''}
                                    render={({ field }) => (
                                        <RHFTextField {...field} label="Storage" placeholder="Enter Storage" type="number" />
                                    )}
                                />
                            </InputInnerContainer>
                              <InputInnerContainer>
                            <Controller
                                name={`variations[${index}].type`}
                                control={control}
                                defaultValue={item.type || 'GB'}
                                render={({ field }) => (
                                <RHFSelect {...field} label="Type">
                                    <MenuItem value="MB">MB</MenuItem>
                                    <MenuItem value="GB">GB</MenuItem>
                                </RHFSelect>
                                )}
                            />
                            </InputInnerContainer>
                            <InputInnerContainer>
                                <Controller
                                    name={`variations[${index}].price`}
                                    control={control}
                                    defaultValue={item.price || ''}
                                    render={({ field }) => (
                                        <RHFTextField {...field} label="Price" placeholder="Enter Price" type="number" />
                                    )}
                                />
                            </InputInnerContainer>
                            <InputInnerContainer>
                                <Controller
                                    name={`variations[${index}].monthly_discount`}
                                    control={control}
                                    defaultValue={item.monthly_discount || ''}
                                    render={({ field }) => (
                                        <RHFTextField {...field} label="Monthly Disc." placeholder="Monthly Disc." type="number" />
                                    )}
                                />
                            </InputInnerContainer>
                            <InputInnerContainer>
                                <Controller
                                    name={`variations[${index}].yearly_discount`}
                                    control={control}
                                    defaultValue={item.yearly_discount || ''}
                                    render={({ field }) => (
                                        <RHFTextField {...field} label="Yearly Disc." placeholder="Yearly Disc." type="number" />
                                    )}
                                />
                            </InputInnerContainer>
                            {index > 0 && <IconButton onClick={() => variationsRemove(index)} color="error">
                                <DeleteIcon />
                            </IconButton>}
                        </Box>
                    ))}
                    </React.Fragment>}
                    {values?.product_id === "Broadcast" && <InputContainer>
                        <Typography variant="h6">Slabs</Typography>
                        <Button onClick={() => slabAppend({ min_calls: '', max_calls: '', price: 0 })}>Add Slabs</Button>
                    </InputContainer>}
                    {values?.product_id === "Broadcast" && slabField.map((item, index) => (
                        <Box key={item.id} display="flex" alignItems="center" gap={2}>
                            <InputInnerContainer>
                                <Controller
                                    name={`slabs[${index}].min_calls`}
                                    control={control}
                                    defaultValue={item.min_calls || ''}
                                    render={({ field }) => (
                                        <RHFTextField {...field} label="Minimum Credits" placeholder="Enter Minium." />
                                    )}
                                />
                            </InputInnerContainer>
                            <InputInnerContainer>
                                <Controller
                                    name={`slabs[${index}].max_calls`}
                                    control={control}
                                    defaultValue={item.max_calls || ''}
                                    render={({ field }) => (
                                        <RHFTextField
                                            {...field}
                                            label="Max Credits"
                                            placeholder="Enter Max Credits."
                                        // multiline
                                        // rows={3}
                                        />
                                    )}
                                />
                            </InputInnerContainer>
                            <InputInnerContainer>
                                <Controller
                                    name={`slabs[${index}].price`}
                                    control={control}
                                    defaultValue={item.price || 0}
                                    render={({ field }) => (
                                        <RHFTextField
                                            {...field}
                                            type={"number"}
                                            label="Price"
                                            placeholder="Enter Price"
                                        // multiline
                                        // rows={3}
                                        />
                                    )}
                                />
                            </InputInnerContainer>
                            {index > 0 && <IconButton onClick={() => slabRemove(index)} color="error">
                                <DeleteIcon />
                            </IconButton>}
                        </Box>
                    ))}

                    {/* <InputContainer>
                        <Typography variant="h6">Features</Typography>
                        <Button onClick={() => append({ feature_name: '', feature_description: '' })}>Add Feature</Button>
                    </InputContainer> */}

                    {/* {fields.map((item, index) => (
                        <Box key={item.id} display="flex" alignItems="center" gap={2}>
                            <InputInnerContainer>
                                <Controller
                                    name={`features[${index}].feature_name`}
                                    control={control}
                                    defaultValue={item.feature_name || ''}
                                    render={({ field }) => (
                                        <RHFTextField {...field} label="Feature Name" placeholder="Enter Feature Name." />
                                    )}
                                />
                            </InputInnerContainer>
                            <InputInnerContainer>
                                <Controller
                                    name={`features[${index}].feature_description`}
                                    control={control}
                                    defaultValue={item.feature_description || ''}
                                    render={({ field }) => (
                                        <RHFTextField
                                            {...field}
                                            label="Feature Description"
                                            placeholder="Enter Feature Description."
                                        // multiline
                                        // rows={3}
                                        />
                                    )}
                                />
                            </InputInnerContainer>
                            <IconButton onClick={() => remove(index)} color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))} */}

                    <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                        <LoadingButton type="submit" variant="contained" loading={loadingButton} sx={{ width: '120px' }}>
                            Submit
                        </LoadingButton>
                    </Stack>
                </Stack>
            </FormProvider>
        </Box>
    );

    return <CustomModal open={open} title="Create Plan" description={description} close={onClose} width={'60%'} />;
};
export default PlanForm;
